import { useCallback, useEffect } from 'react';

import {
  backerKeys,
  useConsumerSubscribeToCause,
  useConsumerUnsubscribeToCause,
} from '@givebacks/api/backer';
import { useForm } from '@givebacks/formy';
import { useQueryClient } from '@tanstack/react-query';
import { IfFeatureState_Core } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { Stack, SwitchCard, notifications } from 'ui';

import { CommunicationLists } from '../../../../Communications/components/CommunicationLists';

interface CommunicationsProps {
  causeId: string;
  status: string;
}

const CommunicationToggle = ({ causeId, status }: { causeId: string; status: string }) => {
  const queryClient = useQueryClient();

  const { mutate: subscribeToCause, isPending: isSubscriptionLoading } =
    useConsumerSubscribeToCause();
  const { mutate: unsubscribeToCause, isPending: isUnsubscriptionLoading } =
    useConsumerUnsubscribeToCause();

  const form = useForm({
    initialValues: {
      receiveCommunications: status === 'subscribed',
    },
    validate: {},
  });

  const onSubscribe = useCallback(() => {
    subscribeToCause(
      { causeId: causeId },
      {
        onSuccess: () => {
          notifications.show({
            message: `You are now subscribed to communications for this organization`,
            variant: 'success',
          });
          const key = backerKeys.consumer.backer.subscriptions.all;
          void queryClient.invalidateQueries({ queryKey: key });
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      }
    );
  }, [subscribeToCause, causeId, queryClient]);

  const onUnsubscribeFromAll = useCallback(() => {
    unsubscribeToCause(
      { causeId: causeId },
      {
        onSuccess: () => {
          notifications.show({
            message: `You are now unsubscribed from communications for this organization`,
            variant: 'success',
          });
          const key = backerKeys.consumer.backer.subscriptions.all;
          void queryClient.invalidateQueries({ queryKey: key });
        },
        onError: (e) => {
          notifications.show({ message: getErrorMessage_v2(e), variant: 'error' });
        },
      }
    );
  }, [unsubscribeToCause, causeId, queryClient]);

  const onToggle = useCallback(() => {
    if (form.values.receiveCommunications) {
      onUnsubscribeFromAll();
    } else {
      onSubscribe();
    }
  }, [form.values.receiveCommunications, onSubscribe, onUnsubscribeFromAll]);

  useEffect(() => {
    form.setFieldValue('receiveCommunications', status === 'subscribed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <SwitchCard
      title="Receive Communications"
      subtitle="By turning off this toggle, you will be opted out of communications but you will continue to receive transactional emails."
      disabled={isSubscriptionLoading || isUnsubscriptionLoading}
      {...form.getInputProps('receiveCommunications', { type: 'checkbox' })}
      onChange={onToggle}
    />
  );
};

const Communications = ({ causeId, status }: CommunicationsProps) => {
  return (
    <Stack>
      <IfFeatureState_Core featureId="user.account_page">
        <CommunicationToggle causeId={causeId} status={status} />
      </IfFeatureState_Core>
      <CommunicationLists causeId={causeId} />
    </Stack>
  );
};

export { Communications };
